import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { createSale } from '../redux/slices/saleSlice';
import { productKey } from './usePosCart';
import { getAvailableStock } from '../utils/formatStock';

/**
 * Builds the exact same sale payload as the POS screen and dispatches the same
 * `createSale` thunk, so Quick Checkout reuses the existing order creation,
 * pricing, payment and inventory deduction path untouched.
 */
export function usePosCheckout({
  cartItems,
  cartPieces,
  subtotal,
  discount = '',
  taxRate = 0,
  customer = null,
  notes = '',
  products = [],
  findStockViolation,
}) {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const shiftStatus = useSelector((state) => state.shift.status);
  const [loading, setLoading] = useState(false);

  const dayClosed = shiftStatus !== 'OPEN';

  const checkout = useCallback(async (paymentData = { method: 'cash' }) => {
    if (dayClosed) {
      toast.error("Today's session is closed. Sales are not allowed.");
      return { ok: false, reason: 'day-closed' };
    }
    if (!cartItems.length) {
      toast.error('Cart is empty');
      return { ok: false, reason: 'empty' };
    }

    const violation = findStockViolation?.();
    if (violation) {
      const key = productKey(violation);
      const product = products.find((p) => String(productKey(p)) === String(key));
      toast.error(product
        ? `Only ${getAvailableStock(product)} ${product.productName || product.name} left – fix the cart before checkout`
        : `${violation.productName || violation.name} is no longer in inventory – remove it from the cart`);
      return { ok: false, reason: 'insufficient-stock', item: violation };
    }

    const discAmt = parseFloat(discount) || 0;
    const totalAmount = subtotal - discAmt + (subtotal - discAmt) * (taxRate / 100);
    const isCredit = paymentData.method === 'credit';

    setLoading(true);
    try {
      const result = await dispatch(
        createSale({
          items: cartItems.map((item) => ({
            productId: item.productId || item._id || item.id,
            productName: item.productName || item.name,
            quantity: item.quantity,
            unitPrice: item.salePrice || item.price || 0,
            itemTotal: (item.salePrice || item.price || 0) * item.quantity,
            saleUnit: item.saleUnit || 'PIECE',
            piecesDeducted: item.piecesDeducted || item.quantity,
          })),
          subtotal,
          discount: discAmt,
          tax: (subtotal - discAmt) * (taxRate / 100),
          total: totalAmount,
          paymentMethod: isCredit ? 'CREDIT' : paymentData.method === 'online' ? 'ONLINE' : 'CASH',
          paymentStatus: isCredit ? 'PENDING' : 'PAID',
          amountPaid: isCredit ? 0 : (paymentData.amountTendered ?? totalAmount),
          amountPending: isCredit ? totalAmount : 0,
          paymentDetails: paymentData,
          customerId: customer?.customerId || customer?.id || customer?._id || null,
          customerName: customer?.fullName || customer?.name || null,
          notes,
          operator: user?.name || user?.fullName || 'Operator',
        })
      ).unwrap();

      toast.success('Sale completed successfully!');
      return { ok: true, sale: result?.sale || result, result };
    } catch (err) {
      // The thunk rejects with the backend's own message, so surface that
      // instead of a generic "Checkout failed".
      const message = (typeof err === 'string' && err)
        || err?.message
        || err?.payload?.message
        || err?.response?.data?.message
        || 'Checkout failed';
      toast.error(message);
      return { ok: false, reason: 'error', error: err, message };
    } finally {
      setLoading(false);
    }
  }, [
    dayClosed, cartItems, findStockViolation, products, dispatch,
    subtotal, discount, taxRate, customer, notes, user,
  ]);

  return { checkout, loading, dayClosed };
}
